"use client"
import { useEffect, useRef, useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import TextWithBr from "@/components/ui/text-with-br"

// Custom Naira Icon Component
function NairaIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M7 4v16" />
      <path d="M17 4v16" />
      <path d="M7 8h10" />
      <path d="M7 12h10" />
      <path d="M4 8h3" />
      <path d="M4 12h3" />
      <path d="M17 8h3" />
      <path d="M17 12h3" />
    </svg>
  )
}

export function AfricanAdvantageSection() {
  const tabs = [
    {
      id: "compliance",
      label: "Compliance",
      color: "primary",
      title: "Built for Healthcare Compliance, Powered by AI",
      description:
        "Stay audit-ready and compliant with intelligent automation that aligns seamlessly with global healthcare regulations so your operations run smoother, safer, and smarter.",
      image: "/images/vectors/compliance.png",
    },
    {
      id: "currencies",
      label: "Multi-Currency",
      color: "secondary",
      title: "Multi-Currency<br /> Support",
      description:
        "Manage pricing, claims, and settlements seamlessly across currencies — with real-time FX updates and automatic localization built in.",
      image: "/images/vectors/multiple-currency.png",
    },
    {
      id: "sms",
      label: "SMS Integration",
      color: "primary",
      title: "AI-Powered Member Communications",
      description:
        "Deliver timely, personalized care experiences through intelligent messaging that keeps every member informed, engaged, and connected.",
      image: "/images/vectors/sms-integration.png",
    },
    {
      id: "standards",
      label: "Local Standards",
      color: "secondary",
      title: "Provider Networks with ICD-10 & CPT",
      description:
        "Map provider data to global code standards to accelerate onboarding and streamline claims operations.",
      image: "/images/vectors/local-standard.png",
    },
  ]

  const [activeTab, setActiveTab] = useState(tabs[0].id)
  const [paused, setPaused] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tabsContainerRef = useRef<HTMLDivElement>(null)

  // Function to scroll active tab into view on mobile
  const scrollActiveTabIntoView = () => {
    if (tabsContainerRef.current && window.innerWidth < 768) {
      const activeTabElement = tabsContainerRef.current.querySelector(`[data-state="active"]`) as HTMLElement
      if (activeTabElement) {
        const container = tabsContainerRef.current
        const containerRect = container.getBoundingClientRect()
        const tabRect = activeTabElement.getBoundingClientRect()
        
        // Calculate scroll position to center the active tab
        const scrollLeft = activeTabElement.offsetLeft - (containerRect.width / 2) + (tabRect.width / 2)
        
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        })
      }
    }
  }

  // Auto-rotate tabs every 5 seconds; pauses on manual interaction and resumes after 5 seconds
  useEffect(() => {
    if (paused) return
    const interval = setInterval(() => {
      setIsTransitioning(true)
      setTimeout(() => {
        setActiveTab((prev) => {
          const currentIndex = tabs.findIndex((t) => t.id === prev)
          const nextIndex = (currentIndex + 1) % tabs.length
          return tabs[nextIndex].id
        })
        setTimeout(() => setIsTransitioning(false), 50)
      }, 200)
    }, 5000)
    return () => clearInterval(interval)
  }, [paused])

  // Clear any pending resume timeout on unmount
  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current)
    }
  }, [])

  // Scroll active tab into view when activeTab changes (mobile only)
  useEffect(() => {
    scrollActiveTabIntoView()
  }, [activeTab])

  const handleTabChange = (value: string) => {
    setIsTransitioning(true)
    setTimeout(() => {
      setActiveTab(value)
      setTimeout(() => setIsTransitioning(false), 50)
    }, 200)
    setPaused(true)
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current)
    resumeTimeoutRef.current = setTimeout(() => setPaused(false), 5000)
  }

  const contentBg: Record<string, string> = {
    primary: "bg-primary/5 border border-primary/10",
    secondary: "bg-secondary/5 border border-secondary/10",
  }

  return (
    <section id="african-advantage" className="relative py-24 bg-gray-50">
      {/* Title area - container-free */}
      <div className="px-4 sm:px-6 lg:px-0">
        <div className="text-center space-y-4 mb-10">
          <h2 className="text-3xl lg:text-5xl font-light text-black">The Octocare Advantage</h2>
          <p className="text-gray-600 max-w-3xl mx-auto mobile-subtitle md:text-xl lg:px-8">
            Built by health insurance experts, Octocare ERP learns, adapts, and scales with your organization. It simplifies complexity, cuts administrative waste, and helps you do more with less, intelligently.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        {/* Tabs navigation - container-free */}
        <div className="w-full overflow-x-auto overflow-y-hidden scrollbar-none" ref={tabsContainerRef}>
          <TabsList className="flex justify-start md:justify-center gap-3 md:gap-6 min-w-max bg-transparent p-0 h-auto">
            {tabs.map((t, idx) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                className={
                  "rounded-full px-4 py-2 md:px-6 md:py-3 text-sm md:text-base transition-colors whitespace-nowrap " +
                  "data-[state=active]:!text-white " +
                  (idx === 0
                    ? "data-[state=active]:!bg-primary" // First tab: primary (blue)
                    : idx === 1
                    ? "data-[state=active]:!bg-secondary" // Second tab: secondary (red)
                    : idx === 2
                    ? "data-[state=active]:!bg-[#f5c236]" // Third tab: #f5c236
                    : "data-[state=active]:!bg-[#38d1bf]") // Fourth tab: #38d1bf
                  + " data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 hover:bg-gray-100"
                }
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Tabs content - in container with slightly increased width for desktop */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">

          {tabs.map((t, idx) => (
            <TabsContent key={t.id} value={t.id} className="mt-8">
              <div 
                className={`
                  rounded-3xl px-8 md:px-9 py-[2.4rem] md:py-[2.1rem] transition-opacity duration-300 ease-in-out
                  ${isTransitioning ? 'opacity-0' : 'opacity-100'}
                  ${idx === 0
                    ? "bg-primary/5 border border-primary/10"
                    : idx === 1
                    ? "bg-secondary/5 border border-secondary/10"
                    : idx === 2
                    ? "bg-[#f5c236]/5 border border-[#f5c236]/10"
                    : "bg-[#38d1bf]/5 border border-[#38d1bf]/10"}
                `}
              >
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="space-y-4 text-center md:text-left">
                    <TextWithBr as="h3" className="text-2xl md:text-3xl font-medium text-black" text={t.title} />
                    <TextWithBr as="p" className="text-gray-700" style={{ fontSize: '1.098rem' }} text={t.description} />
                  </div>
                  <div>
                    <img
                      src={t.image}
                      alt={t.title}
                      className="w-full h-auto object-cover rounded-none shadow-none border-0"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </div>
      </Tabs>
      
      <style jsx>{`
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-none {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .mobile-subtitle {
          font-size: 1rem; /* further slight reduction for mobile */
        }
        @media (min-width: 768px) {
          .mobile-subtitle {
            font-size: 1.25rem;
          }
        }
      `}</style>
    </section>
  )
}
